import { createContext, useContext, useMemo, type PropsWithChildren } from "react";
import { useMutation } from "convex/react";
import type {
  VoiceNoteSurface,
  VoiceStartSessionResult,
  VoiceTranscriptionMode,
} from "@papersflow/contracts/voice-notes";
import { api } from "../../../../backend/convex/_generated/api";
import type { Id } from "../../../../backend/convex/_generated/dataModel";

export interface VoiceSessionApi {
  readonly startSession: (input: {
    readonly workspaceId: Id<"workspaces">;
    readonly projectId: Id<"projects">;
    readonly surface: VoiceNoteSurface;
    readonly mode: VoiceTranscriptionMode;
  }) => Promise<VoiceStartSessionResult>;
  readonly appendTurn: (input: {
    readonly workspaceId: Id<"workspaces">;
    readonly projectId: Id<"projects">;
    readonly noteId: Id<"voiceNotes">;
    readonly text: string;
    readonly startMs?: number;
    readonly endMs?: number;
    readonly speaker?: string;
  }) => Promise<null>;
  readonly completeSession: (input: {
    readonly workspaceId: Id<"workspaces">;
    readonly projectId: Id<"projects">;
    readonly noteId: Id<"voiceNotes">;
    readonly durationMs?: number;
  }) => Promise<null>;
  readonly failSession: (input: {
    readonly workspaceId: Id<"workspaces">;
    readonly projectId: Id<"projects">;
    readonly noteId: Id<"voiceNotes">;
    readonly message: string;
  }) => Promise<null>;
  readonly attachAudio: (input: {
    readonly workspaceId: Id<"workspaces">;
    readonly projectId: Id<"projects">;
    readonly noteId: Id<"voiceNotes">;
    readonly audioKey: string;
  }) => Promise<null>;
}

const VoiceSessionApiContext = createContext<VoiceSessionApi | null>(null);

export function VoiceSessionApiProvider({ children }: PropsWithChildren) {
  const startSession = useMutation(api.voiceNotes.startSession);
  const appendTurn = useMutation(api.voiceNotes.appendTurn);
  const completeSession = useMutation(api.voiceNotes.completeSession);
  const failSession = useMutation(api.voiceNotes.failSession);
  const attachAudio = useMutation(api.voiceNotes.attachAudio);
  const value = useMemo(
    () => ({
      startSession,
      appendTurn,
      completeSession,
      failSession,
      attachAudio,
    }),
    [appendTurn, attachAudio, completeSession, failSession, startSession],
  );
  return <VoiceSessionApiContext.Provider value={value}>{children}</VoiceSessionApiContext.Provider>;
}

export function useVoiceSessionApi(): VoiceSessionApi | null {
  return useContext(VoiceSessionApiContext);
}
